<template>
  <v-card
    outlined
    class="admin-table-card dimension-report"
  >
    <div class="dimension-report__heading">
      <div>
        <h2>{{ title }}</h2><p class="text-caption text--secondary mb-0">
          {{ subtitle }}
        </p>
      </div>
      <v-btn
        v-if="compact"
        text
        small
        color="primary"
        @click="$emit('expand', dimension)"
      >
        Raporu aç
      </v-btn>
      <v-select
        v-else
        v-model="sort"
        :items="sorts"
        label="Sıralama"
        outlined
        dense
        hide-details
        class="dimension-report__sort"
        @change="resetPage"
      ></v-select>
    </div>
    <div
      v-if="!compact"
      class="dimension-report__totals"
      aria-label="Rapor toplamları"
    >
      <div
        v-for="metric in totals"
        :key="metric.label"
      >
        <span class="text-caption text--secondary">{{ metric.label }}</span><strong>{{ report && !loading ? number(metric.value) : '—' }}</strong>
      </div>
    </div>
    <v-alert
      v-if="error"
      type="error"
      text
      dense
      class="ma-4"
      role="alert"
    >
      {{ error }} <v-btn
        text
        small
        @click="fetchReport"
      >
        Tekrar dene
      </v-btn>
    </v-alert>
    <v-data-table
      loading-text="Rapor yükleniyor…"
      :headers="headers"
      :items="rows"
      :loading="loading"
      :items-per-page="size"
      :server-items-length="totalRows"
      hide-default-footer
      :mobile-breakpoint="760"
      class="admin-data-table"
      @click:row="inspect"
    >
      <template #[`item.key`]="{ item }">
        <button
          class="dimension-report__name"
          type="button"
          @click.stop="inspect(item)"
        >
          {{ rowLabel(item) }}
        </button>
        <div class="text-caption text--secondary dimension-report__secondary">
          {{ dimension === 'users' ? (item.email || 'E-posta eşleşmesi yok') : (dimension === 'screens' ? item.key : '') }}
        </div>
      </template>
      <template #[`item.views`]="{ item }">
        {{ number(item.views) }}
      </template>
      <template #[`item.events`]="{ item }">
        {{ number(item.events) }}
      </template>
      <template #[`item.activeUsers`]="{ item }">
        {{ number(item.activeUsers) }}
      </template>
      <template #[`item.sessions`]="{ item }">
        {{ number(item.sessions) }}
      </template>
      <template #[`item.perUser`]="{ item }">
        {{ item.activeUsers ? decimal((dimension === 'screens' ? item.views : item.events) / item.activeUsers) : '—' }}
      </template>
      <template #[`item.lastSeen`]="{ item }">
        <span class="text-caption">{{ date(item.lastSeen) }}</span>
      </template>
      <template #no-data>
        <div class="dimension-report__empty">
          {{ error ? 'Rapor yüklenemedi.' : 'Bu filtrelerle eşleşen kayıt yok.' }}
        </div>
      </template>
    </v-data-table>
    <div
      v-if="!compact"
      class="dimension-report__footer"
    >
      <span class="text-caption text--secondary">{{ totalRows ? `${number(page * size + 1)}–${number(Math.min((page + 1) * size, totalRows))} / ${number(totalRows)}` : '0' }} {{ dimension === 'users' ? 'kullanıcı' : 'kayıt' }}</span><v-spacer></v-spacer>
      <v-select
        v-model="size"
        :items="[10, 25, 50, 100]"
        label="Satır / sayfa"
        outlined
        dense
        hide-details
        class="dimension-report__size"
        @change="resetPage"
      ></v-select>
      <v-btn
        text
        small
        :disabled="!page || loading"
        @click="go(page - 1)"
      >
        Önceki
      </v-btn><span class="text-caption">{{ page + 1 }} / {{ pages }}</span><v-btn
        text
        small
        :disabled="page + 1 >= pages || loading"
        @click="go(page + 1)"
      >
        Sonraki
      </v-btn>
    </div>
    <p class="text-caption text--secondary ma-4">
      {{ dimension === 'users' ? 'Kullanıcılar ID ile tekilleştirilir; anonim eventler bu listede yer almaz.' : 'Satırlar arasında aynı kullanıcı bulunabilir; tekil kullanıcı toplamı satırların toplamı değildir.' }} Satıra tıklayarak ilgili event kayıtlarını inceleyin.
    </p>
  </v-card>
</template>

<script>
import axios from 'axios'
import { getAccessToken } from '@/utils/panelSession'
import { eventParams, formatEventDate, screenLabel } from '@/utils/analyticsEvents'

export default {
  props: {
    dimension: { type: String, required: true },
    filters: { type: Object, required: true },
    refreshKey: { type: Number, default: 0 },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      report: null, loading: false, error: '', page: 0, size: this.compact ? 5 : 25, sort: this.dimension === 'screens' ? 'views,desc' : 'events,desc', request: 0, cancel: null,
    }
  },
  computed: {
    querySignature() { return JSON.stringify({ filters: this.filters, refresh: this.refreshKey }) },
    title() { return { screens: 'Sayfalar ve ekranlar', events: 'Event raporu', users: 'Kullanıcı raporu' }[this.dimension] },
    subtitle() { return { screens: 'Hangi ekranlar görülüyor ve ne kadar etkileşim alıyor?', events: 'Event adına göre hacim, kullanıcı ve oturum dağılımı.', users: 'Kullanıcı ID, e-posta ve aktivite özeti.' }[this.dimension] },
    rows() { return this.report?.rows?.content || [] },
    totalRows() { return Number(this.report?.rows?.totalElements || 0) },
    pages() { return Math.max(1, Math.ceil(this.totalRows / this.size)) },
    totals() {
      const t = this.report?.totals || {}

      return [{ label: 'Event', value: t.events }, { label: 'Aktif kullanıcı', value: t.activeUsers }, { label: 'Ekran görüntüleme', value: t.views }, { label: 'Oturum', value: t.sessions }]
    },
    sorts() { return [{ text: 'En çok event', value: 'events,desc' }, { text: 'En çok görüntüleme', value: 'views,desc' }, { text: 'En çok aktif kullanıcı', value: 'activeUsers,desc' }, { text: 'Son aktivite', value: 'lastSeen,desc' }] },
    headers() {
      const identity = {
        text: { screens: 'Sayfa / ekran', events: 'Event adı', users: 'Kullanıcı' }[this.dimension], value: 'key', width: this.compact ? '50%' : '29%', sortable: false,
      }
      const metrics = this.dimension === 'screens' ? [{ text: 'Görüntüleme', value: 'views' }, { text: 'Aktif kullanıcı', value: 'activeUsers' }] : [{ text: 'Event', value: 'events' }, { text: this.dimension === 'users' ? 'Görüntüleme' : 'Aktif kullanıcı', value: this.dimension === 'users' ? 'views' : 'activeUsers' }]
      if (!this.compact) metrics.push({ text: 'Oturum', value: 'sessions' }, ...(this.dimension === 'users' ? [] : [{ text: this.dimension === 'screens' ? 'Görüntüleme / aktif kullanıcı' : 'Event / aktif kullanıcı', value: 'perUser' }]), { text: 'Son aktivite (TR)', value: 'lastSeen' })

      return [identity, ...metrics.map(item => ({ ...item, sortable: false, align: 'end' }))]
    },
  },
  watch: {
    querySignature(next, previous) {
      if (JSON.stringify(JSON.parse(next).filters) !== JSON.stringify(JSON.parse(previous).filters)) this.page = 0
      this.fetchReport()
    },
  },
  mounted() { this.fetchReport() },
  beforeDestroy() { this.request += 1; if (this.cancel) this.cancel.cancel() },
  methods: {
    async fetchReport() {
      this.request += 1
      const { request } = this
      if (this.cancel) this.cancel.cancel()
      this.cancel = axios.CancelToken.source()
      this.loading = true; this.error = ''; this.report = null
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/analytics/events/reports/${this.dimension}`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
          timeout: 30000,
          cancelToken: this.cancel.token,
          params: {
            ...eventParams(this.filters), page: this.page, size: this.size, sort: this.sort,
          },
        })
        if (request === this.request) this.report = response.data.data
      } catch (error) {
        if (request === this.request && !axios.isCancel(error)) this.error = error.response?.data?.message || 'Rapor yüklenemedi.'
      } finally { if (request === this.request) this.loading = false }
    },
    resetPage() { this.page = 0; this.fetchReport() },
    go(page) { this.page = page; this.fetchReport() },
    inspect(row) { this.$emit('inspect', { dimension: this.dimension, key: row.key }) },
    rowLabel(row) {
      if (this.dimension === 'screens') return screenLabel(row.key)
      if (this.dimension === 'users') return `#${row.key} ${row.userName || ''}`

      return row.key
    },
    number(value) { return new Intl.NumberFormat('tr-TR').format(Number(value || 0)) },
    decimal(value) { return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value) },
    date(value) { return `${formatEventDate(value)} ${formatEventDate(value, true)}` },
  },
}
</script>

<style lang="scss" scoped>
.dimension-report {
  min-width: 0;
  --report-heading: #172033;
  h2, strong { color: var(--report-heading); }
  h2 { font-size: 16px; margin: 0 0 5px; }
  &__heading, &__footer { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 20px; }
  &__heading { justify-content: space-between; }
  &__sort { max-width: 215px; }
  &__size { max-width: 110px; }
  &__totals { display: grid; grid-template-columns: repeat(4, 1fr); padding: 0 20px 20px; gap: 16px; }
  &__totals > div { display: flex; flex-direction: column; gap: 5px; }
  &__totals strong { font-size: 24px; font-variant-numeric: tabular-nums; }
  &__name { text-align: left; font-weight: 600; overflow-wrap: anywhere; color: inherit; }
  &__name:hover, &__name:focus-visible { color: var(--v-primary-base); text-decoration: underline; }
  &__secondary { overflow-wrap: anywhere; }
  &__empty { padding: 35px; }
  ::v-deep td { padding-top: 14px !important; padding-bottom: 14px !important; font-variant-numeric: tabular-nums; cursor: pointer; }
  @media (max-width: 760px) { &__totals { grid-template-columns: 1fr 1fr; } &__sort { max-width: 100%; } }
}
.dimension-report.theme--dark { --report-heading: #f1f5f9; }
</style>
